import utils from '@bigcommerce/stencil-utils';
import createLogger, { generateUUID } from '../../common/utils/logger';
import { setWithExpiry, getWithExpiry } from '../../common/utils/storage-utils';
import { consentObject, cleanMetaData, loyaltyProgramField } from '../../common/utils/cookie-utils';

/**
 * Check if an object has at least one valid expected key with a non-null, non-empty value
 * @param {Object} obj - The object to check
 * @param {Array<string>} keys - Array of expected keys
 * @returns {boolean} - True if at least one expected key has a valid value
 */
function hasValidExpectedKey(obj, keys) {
  if (!obj || typeof obj !== 'object') return false;

  const allowed = new Set(keys);

  return Object.keys(obj).some(key => {
    if (!allowed.has(key)) return false;

    const val = obj[key];
    if (val == null) return false;

    if (typeof val === 'string' && val.trim() === '') return false;

    return true;
  });
}

// =============================================================
// Enhanced compareMetricsFile() with Unified Logger
// =============================================================
function compareMetricsFile(newMetrics, oldMetrics) {
  const log = createLogger('MetricsCompare', generateUUID());
  log.group('Comparing metrics');
  log.pmark('start');

  // Guard clauses
  if (typeof newMetrics !== 'object' || newMetrics === null) {
    log.warn('Invalid newMetrics; expected object', { newMetrics });
    log.groupEnd();
    return false;
  }
  if (typeof oldMetrics !== 'object' || oldMetrics === null) {
    log.info('No previous metrics; treating as updated', { oldMetrics });
    log.groupEnd();
    return true;
  }

  const allKeys = new Set([...Object.keys(newMetrics), ...Object.keys(oldMetrics)]);
  let changed = false;
  const differences = [];

  for (const key of allKeys) {
    const newVal = newMetrics[key];
    const oldVal = oldMetrics[key];
    if (newVal !== oldVal) {
      changed = true;
      differences.push({ key, oldVal, newVal });
    }
  }

  if (changed) {
    log.info(`Metrics changed on ${differences.length} keys`, differences);
  } else {
    log.debug('No differences detected between metrics');
  }

  log.pmark('end');
  log.groupEnd();
  return changed;
}

// =============================================================
// Enhanced saveSiteErrorsFile() with Unified Logger and timing
// =============================================================
const SESSION_STORAGE_LIMIT = 65535; // Approximate sessionStorage limit in characters

function saveSiteErrorsFile(data) {
  const log = createLogger('SaveSiteErrors', generateUUID());
  log.group('Saving siteErrors data');
  log.pmark('start');

  let serializedData;
  try {
    serializedData = JSON.stringify(data);
    log.debug(`Initial serialized size: ${serializedData.length.toLocaleString()} chars`);
  } catch (e) {
    log.error('Error serializing siteErrors data to JSON', e);
    log.groupEnd();
    return;
  }

  if (serializedData.length > SESSION_STORAGE_LIMIT) {
    log.error('Data size still exceeds sessionStorage limit; potential truncation risk', {
      finalSize: serializedData.length,
      limit: SESSION_STORAGE_LIMIT,
    });
  }

  try {
    sessionStorage.setItem('siteErrors', serializedData);
    log.info('Saved siteErrors to sessionStorage', {
      storedLength: serializedData.length,
    });
  } catch (e) {
    log.error('Error saving siteErrors to sessionStorage', e);
    // Optional fallback to localStorage
    try {
      localStorage.setItem('siteErrors', serializedData);
      log.warn('Fallback: saved siteErrors to localStorage');
    } catch (fallbackError) {
      log.error('Fallback localStorage save failed', fallbackError);
    }
  }

  log.pmark('end');
  log.groupEnd();
}

// =============================================================
// Data Layer Integration
// =============================================================

// Example: integrated logger into onlineServiceAnalyticsResourceBuilder
export function onlineServiceAnalyticsResourceBuilderFile() {
  const log = createLogger('OnlineServiceAnalytics', 'init');
  log.group('Starting Resource Builder');
  log.pmark('start');

  let localData;
  const localStoredData = localStorage.getItem('onlineServicesHealth');
  if (localStoredData) {
    try {
      localData = JSON.parse(localStoredData);
      log.debug('Loaded local data', localData);
    } catch (e) {
      log.error('Error parsing local data', e);
      localData = getDefaultData("local");
    }
  } else {
    log.info('No local data found; creating new entry');
    localData = getDefaultData("local");
  }

  let sessionData;
  const sessionStoredData = sessionStorage.getItem('onlineServicesHealth');
  if (sessionStoredData) {
    try {
      sessionData = JSON.parse(sessionStoredData);
      log.debug('Loaded session data', sessionData);
    } catch (e) {
      log.error('Error parsing session data', e);
      sessionData = getDefaultData("session");
    }
  } else {
    sessionData = getDefaultData("session");
    log.info('No session data found; initialized with defaults');
  }

  function getDefaultData(storageType) {
    log.debug(`Generating default data for ${storageType}`);
    if (storageType === "session") {
      return { onlineServicesHealthSession: { SessionId: generateUUID() } };
    }
    return {
      onlineServicesHealthLocal: {
        DeviceId: generateUUID(),
        VisitorId: "",
        VisitorIds: [],
      },
    };
  }

  const uniqueId = localStorage.getItem('uniqueId');
  if (uniqueId != null) {
    const v = localData.onlineServicesHealthLocal;
    if (v.VisitorId === '' || v.VisitorId !== uniqueId) {
      v.VisitorId = uniqueId;
      v.VisitorIds = arrayCompareAndAddIfDifferent(v.VisitorIds || [], uniqueId);
      log.info('Updated VisitorId', { VisitorId: v.VisitorId, VisitorIds: v.VisitorIds });
    }
  }
  
  log.debug('Final localData', localData);
  log.debug('Final sessionData', sessionData);

  sessionStorage.setItem('onlineServicesHealth', JSON.stringify(sessionData));
  localStorage.setItem('onlineServicesHealth', JSON.stringify(localData));
  log.pmark('dataStored');

  saveVisitQueryParams?.();
  log.groupEnd();
}

// =============================================================
// Example Integration: processEvent()
// =============================================================
export function processEventFile(newEvent, tealium_eventData) {
  const log = createLogger('ProcessEvent', newEvent?.errorMessage?.eventName || 'event');
  log.group('Processing event');
  log.pmark('start');

  let data;
  const storedData = sessionStorage.getItem('siteErrors');
  if (storedData) {
    try {
      data = JSON.parse(storedData);
      log.debug('Loaded siteErrors from sessionStorage');
    } catch (e) {
      log.error('Error parsing siteErrors JSON', e);
      data = getDefaultData();
    }
  } else {
    data = getDefaultData();
    log.info('No existing siteErrors data found; using defaults');
  }

  const userAgent = navigator.userAgent;
  const cartId = getCartId();
  const ip = window.BC_CONTEXT.client_ip_address;
  buildOnlineServiceAnalyticsEventFile(newEvent, tealium_eventData, cartId);
  log.debug('Built online service event', { cartId });

  const failures = data.siteErrors.tealiumFailures;
  log.debug('Loaded failures object', failures);

  const adAttribution = getWithExpiry("latestQueryStringParameters") || {};
  failures.adAttribution = adAttribution;

  failures.ip_address = arrayCompareAndAddIfDifferent(failures.ip_address || [], ip);
  failures.userAgent = arrayCompareAndAddIfDifferent(failures.userAgent || [], userAgent);
  if (cartId) failures.cartId = arrayCompareAndAddIfDifferent(failures.cartId || [], cartId);

  failures.consent = consentObject();

  saveSiteErrorsFile(data);
  log.pmark('saved');
  log.groupEnd();
}


/**
 * Build Online Service Analytics Event
 */
export async function buildOnlineServiceAnalyticsEventFile(newEvent, tealium_eventData, cartId) {
  const start = performance.now();
  const log = createLogger('BuildAnalyticsEvent', 'build');
  log.groupCollapsed(
    `[Analytics] buildOnlineServiceAnalyticsEventFile → ${tealium_eventData?.tealium_event || "unknown"}`
  );

  try {
    function mapEventToType(eventName) {
      const pageActions = [
        'add_to_cart_start', 'add_to_cart', 'add_to_cart_click', 'add_to_favorites', 'catalog_request',
        'header_logo_click', 'new_store_guide_request', 'order_receipt', 'order_shipping',
        'preference_center_submit', 'product review', 'product_click', 'shipping_option_selected',
        'shipping_options_displayed', 'swatch_click', 'image_click', 'name_click', 'star_review_click',
        'subcategory_click', 'reco_ready', 'checkout_complete','checkout_start','login'
      ];
      const pageViews = [
        'account', 'cart', 'content', 'home', 'order billing', 'order_payment',
        'order_shipping', 'product_detail', 'product_listing', 'search results', '404'
      ];

      if (typeof eventName !== 'string' || !eventName) {
        log.warn('[Analytics] mapEventToType received invalid eventName:', eventName);
        return 'unknown';
      }

      const name = eventName.toLowerCase();
      if (pageActions.includes(name)) return 'pageAction';
      if (pageViews.includes(name)) return 'pageView';

      log.warn('[Analytics] mapEventToType could not match event:', name);
      return 'unknown';
    }

    const ip = window.BC_CONTEXT.client_ip_address;
    const clientIpAddress = await getIPAddress();

    const appId = window.BC_CONTEXT.appId;
    const userAgent = navigator.userAgent;

    const localData = log.safeParse(localStorage.getItem('onlineServicesHealth'), 'localStorage');
    const sessionData = log.safeParse(sessionStorage.getItem('onlineServicesHealth'), 'sessionStorage');
    // getWithExpiry already returns parsed object, no need for safeParse
    const adAttribution = getCurrentTrackingParams() || {};

    let metaData = cleanMetaData({
      ...tealium_eventData,
      cartId,
      userAgent,
      ...adAttribution,
      ...loyaltyProgramField(),
      errorMessage: newEvent?.errorMessage?.eventMessage,
      timeStamp: newEvent?.errorMessage?.timeStamp
    });

    const onlineServiceEvent = {
      ...(localData?.onlineServicesHealthLocal || {}),
      ...(sessionData?.onlineServicesHealthSession || {}),
      Host: window.location.host,
      FullURL: window.location.href,
      Name: tealium_eventData?.tealium_event,
      EventType: mapEventToType(tealium_eventData?.tealium_event),
      ApplicationId: appId,
      ServedIpAddress: ip,
      ClientIpAddress: clientIpAddress,
      metaData
    };

    // A nameless event is rejected by the backend with a 400: JSON.stringify drops
    // undefined values, so the POST body would carry no `Name` key at all. Drop it here
    // rather than spending a round-trip to be refused.
    if (!onlineServiceEvent.Name) {
      log.warn('[Analytics] Not sending event with no Name:', onlineServiceEvent);
      return;
    }

    if (onlineServiceEvent.EventType === 'pageView') {
      log.debug('[Analytics] PageView attributes prepared:', onlineServiceEvent);
      // Also not awaited (it can park until the Noibu SDK is ready), so its rejection
      // has to be handled here — otherwise a throwing NOIBUJS.addCustomAttribute escapes
      // this try/catch as an unhandled rejection. Noibu attribution is best-effort: a
      // failure here must not stop the analytics event below from being sent.
      checkSDKExistanceAndAddCustomAttribute(onlineServiceEvent).catch(err => {
        log.error('[Analytics] Error adding Noibu custom attributes:', err);
      });
    }

    // Not awaited (the builder is fire-and-forget), so the rejection has to be handled
    // here — otherwise a failed send escapes this try/catch as an unhandled rejection.
    sendOnlineServiceAnalyticsEvent(onlineServiceEvent).catch(err => {
      log.error('[Analytics] Error sending analytics event:', err);
    });

    const time = (performance.now() - start).toFixed(2);
    log.log(`[Analytics] Event built in ${time}ms`);
  } catch (err) {
    log.error('[Analytics] Error building analytics event:', err);
  } finally {
    log.groupEnd();
  }
}
async function checkSDKExistanceAndAddCustomAttribute(onlineServiceEvent) {
  const log = createLogger('CheckSDK');
  if (!window.NOIBUJS) {
    await new Promise(resolve => {
      window.addEventListener('noibuSDKReady', resolve);
    });
  }
//   let localData = {};
//     const localStoredData = localStorage.getItem('onlineServicesHealth');
//     if (localStoredData) {
//         try {
//             localData = JSON.parse(localStoredData);
//         } catch (e) {
//             console.error('Error parsing stored onlineServicesHealth data from localStorage:', e);
//         }
//     }

//     let sessionData;
//     const sessionStoredData = sessionStorage.getItem('onlineServicesHealth');
//     if (sessionStoredData) {
//         try {
//             sessionData = JSON.parse(sessionStoredData);
//         } catch (e) {
//             console.error('Error parsing stored siteErrors data from sessionStorage:', e);

//         }
//     } 

    const uniqueId = localStorage.getItem('uniqueId');

    if(onlineServiceEvent.EventType == 'pageView'){
        const keyValue = [];
        
        keyValue.push({name:'CustomSessionId',id: onlineServiceEvent.SessionId});
        keyValue.push({name:'DeviceId',id: onlineServiceEvent.DeviceId});
        if(onlineServiceEvent.VisitorId != null && onlineServiceEvent.VisitorId != ''){
            keyValue.push({name:'VisitorId',id: onlineServiceEvent.VisitorId});
        }
        if(typeof cartId !="undefined" && cartId != null){
            keyValue.push({name:'cartId',id: cartId});
        }
        if(uniqueId != null){
        keyValue.push({name:'user_id',id: uniqueId});
        }
        
       
        log.debug("onlineServiceEvent Data: ",onlineServiceEvent);
        for(let u = 0; u < keyValue.length;u++){
            window.NOIBUJS.addCustomAttribute(keyValue[u].name, keyValue[u].id);
        }
        
    }


}

/**
 * Compare array and add new value if different
 * Handles both primitive values (strings, numbers) and arrays of objects
 * @param {Array} targetArr - The target array to add to
 * @param {*} sourceValue - Either a primitive value or an array of objects to compare
 * @param {string} key - The key to use for comparing objects (default: 'id')
 * @returns {Array} The updated target array
 */
function arrayCompareAndAddIfDifferent(targetArr, sourceValue, key = 'id') {
  const start = performance.now();
  const log = createLogger('ArrayCompare');
  log.groupCollapsed(
    `[ArrayCompare] Comparing arrays (target length: ${targetArr?.length || 0})`
  );

  try {
    // Ensure targetArr is an array
    if (!Array.isArray(targetArr)) {
      log.warn('[ArrayCompare] targetArr is not an array, initializing as empty array.');
      targetArr = [];
    }

    // Handle primitive values (strings, numbers, etc.)
    if (!Array.isArray(sourceValue)) {
      log.debug('[ArrayCompare] Source is a primitive value:', sourceValue);

      // Check if the value already exists in the target array
      if (!targetArr.includes(sourceValue)) {
        log.info('[ArrayCompare] Adding new primitive value:', sourceValue);
        targetArr.push(sourceValue);
      } else {
        log.debug('[ArrayCompare] Primitive value already exists, skipping.');
      }

      const time = (performance.now() - start).toFixed(2);
      log.log(`[ArrayCompare] Completed in ${time}ms`);
      log.groupEnd();
      return targetArr;
    }

    // Handle arrays of objects
    log.debug('[ArrayCompare] Source is an array, comparing objects by key:', key);

    // Create a Set of existing keys from target array
    const targetKeys = new Set(targetArr.map(item =>
      typeof item === 'object' && item !== null ? item[key] : item
    ));

    // Filter source array to find items NOT in target
    const added = sourceValue.filter(item => {
      const itemKey = typeof item === 'object' && item !== null ? item[key] : item;
      return !targetKeys.has(itemKey);
    });

    if (added.length > 0) {
      log.info(`[ArrayCompare] ${added.length} new items found; adding to target.`);
      targetArr.push(...added);
    } else {
      log.debug('[ArrayCompare] No new differences detected.');
    }

    const time = (performance.now() - start).toFixed(2);
    log.log(`[ArrayCompare] Completed in ${time}ms`);
  } catch (err) {
    log.error('[ArrayCompare] Error comparing arrays:', err);
  } finally {
    log.groupEnd();
  }

  return targetArr;
}
/**
 * Get the list of expected tracking parameter keys
 * @returns {Array<string>} Array of expected parameter keys
 */
function getExpectedTrackingKeys() {
    return [
        // Standard UTM parameters
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",

        // Google Ads
        "gclid",           // Google Click ID
        "gad_source",      // Google Ads source
        "GBRAID",          // Google iOS tracking
        "WBRAID",          // Google web-to-app tracking
        "adgroupid",       // Ad group identifier
        "targetid",        // Target identifier
        "campaignid",      // Campaign identifier
        "content",         // Content identifier
        "srsltid",         // Google Shopping click ID
        "dclid",           // DoubleClick click ID

        // Microsoft/Bing Ads
        "msclkid",         // Microsoft Click ID
        "mscrid",          // Microsoft Creative ID

        // Meta/Facebook/Instagram
        "fbclid",          // Facebook Click ID
        "igshid",          // Instagram Share ID

        // TikTok
        "ttclid",          // TikTok Click ID
        "ttadid",          // TikTok Ad ID
        "ttcid",           // TikTok Campaign ID
        "ttsid",           // TikTok Session ID
        "ttclid_exp",      // TikTok Click ID expiration

        // Pinterest
        "epik",            // Pinterest Click ID

        // Snapchat
        "ScCid",           // Snapchat Click ID
        "_scid",           // Snapchat Cookie ID (alternative)

        // Reddit
        "rdt_cid",         // Reddit Click ID

        // LinkedIn
        "li_fat_id",       // LinkedIn First Ad Touch ID

        // Twitter/X
        "twclid",          // Twitter Click ID

        // YouTube
        "yclid",           // YouTube Click ID

        // Amazon
        "maas",            // Amazon Attribution Service

        // Email Marketing
        "mc_cid",          // Mailchimp Campaign ID
        "eid",             // Email ID (generic)

        // General
        "ref",             // Referral parameter
        "referrer"         // Alternative referral parameter
    ];
}

/**
 * Get current query parameters from URL that match expected tracking keys
 * @param {boolean} filterNull - If true, only return parameters with values (default: true)
 * @returns {Object|null} Object with tracking parameters found in current URL, or null if none found
 */
function getCurrentTrackingParams(filterNull = true) {
    const log = createLogger('TrackingParams');
    log.groupCollapsed('[getCurrentTrackingParams] Getting current URL tracking parameters');

    try {
        const expectedKeys = getExpectedTrackingKeys();

        // Get navigation entry from Performance API
        const navEntry = performance.getEntriesByType('navigation')?.[0];
        if (!navEntry) {
            log.warn('[getCurrentTrackingParams] Navigation entry not available, falling back to window.location');
            // Fallback to window.location.href
            const currentUrl = window.location.href;
            const queryParams = parseQueryStringToObjectWithDefaultKeys(currentUrl, expectedKeys);

            if (filterNull) {
                const filtered = Object.fromEntries(
                    Object.entries(queryParams).filter(([key, value]) => value !== null && value !== '')
                );
                log.debug('[getCurrentTrackingParams] Found parameters (filtered):', filtered);
                log.groupEnd();
                return Object.keys(filtered).length > 0 ? filtered : null;
            }

            log.debug('[getCurrentTrackingParams] Found parameters (raw):', queryParams);
            log.groupEnd();
            return queryParams;
        }

        // Parse current page query parameters
        const queryParams = parseQueryStringToObjectWithDefaultKeys(navEntry.name, expectedKeys);
        log.debug('[getCurrentTrackingParams] Current query params (raw):', queryParams);

        if (filterNull) {
            // Filter out null values - only keep parameters that have actual values
            const filtered = Object.fromEntries(
                Object.entries(queryParams).filter(([key, value]) => value !== null && value !== '')
            );
            log.debug('[getCurrentTrackingParams] Current query params (filtered):', filtered);
            log.groupEnd();
            return Object.keys(filtered).length > 0 ? filtered : null;
        }

        log.groupEnd();
        return queryParams;
    } catch (err) {
        log.error('[getCurrentTrackingParams] Error getting current tracking params:', err);
        log.groupEnd();
        return null;
    }
}

function saveVisitQueryParams(key = "firstVisitParams", expirationDays = 30, domain, paramsWhitelist = null) {
    const log = createLogger('SaveVisitParams');
    log.groupCollapsed('[saveVisitQueryParams] Processing query parameters');

    const expectedKeys = getExpectedTrackingKeys();

    // Get navigation entry from Performance API
    const navEntry = performance.getEntriesByType('navigation')?.[0];
    if (!navEntry) {
        log.warn('[saveVisitQueryParams] Navigation entry not available');
        log.groupEnd();
        return;
    }

    // Parse current page query parameters
    const queryParamsCurrent = parseQueryStringToObjectWithDefaultKeys(navEntry.name, expectedKeys);
    log.debug('[saveVisitQueryParams] Current query params (raw):', queryParamsCurrent);

    // Filter out null values - only keep parameters that have actual values
    const queryParamsCurrentFiltered = Object.fromEntries(
        Object.entries(queryParamsCurrent).filter(([key, value]) => value !== null && value !== '')
    );
    log.debug('[saveVisitQueryParams] Current query params (filtered):', queryParamsCurrentFiltered);

    const currentPageHasValuedParam = hasValidExpectedKey(queryParamsCurrent, expectedKeys);
    log.debug('[saveVisitQueryParams] Current page has valid param:', currentPageHasValuedParam);

    // Handle redirect scenario
    if (navEntry && navEntry.redirectCount > 0 && window.navigation?.activation?.from?.url) {
        const queryParamsRedirectPage = parseQueryStringToObjectWithDefaultKeys(window.navigation.activation.from.url, expectedKeys);

        // Filter out null values from redirect page params
        const queryParamsRedirectPageFiltered = Object.fromEntries(
            Object.entries(queryParamsRedirectPage).filter(([key, value]) => value !== null && value !== '')
        );

        const RedirectPageHasValuedParam = hasValidExpectedKey(queryParamsRedirectPage, expectedKeys);
        log.debug('[saveVisitQueryParams] Redirect page has valid param:', RedirectPageHasValuedParam);
        log.debug('[saveVisitQueryParams] Redirect page params (filtered):', queryParamsRedirectPageFiltered);
    }

    // Only proceed if current page has valued parameters
    if (currentPageHasValuedParam === true) {
        log.info('[saveVisitQueryParams] Found valid query parameters, saving to storage...');

        // Get existing data using getWithExpiry (checks session then local storage)
        let latestQueryStringParametersParsed = getWithExpiry("latestQueryStringParameters");
        let queryParamsLocalStorageParsed = null;

        const queryParamsLocalStorage = localStorage.getItem("queryParamsArrayLocalStorage");
        if (queryParamsLocalStorage != null) {
            try {
                queryParamsLocalStorageParsed = JSON.parse(queryParamsLocalStorage);
            } catch (e) {
                log.error('[saveVisitQueryParams] Error parsing queryParamsArrayLocalStorage:', e);
            }
        }

        let newVal = false;
        if (latestQueryStringParametersParsed != null) {
            newVal = compareMetricsFile(latestQueryStringParametersParsed, queryParamsCurrentFiltered);
            log.debug('[saveVisitQueryParams] Parameters changed:', newVal);

            if (newVal) {
                if (queryParamsLocalStorageParsed != null) {
                    queryParamsLocalStorageParsed.push(queryParamsCurrentFiltered);
                } else {
                    queryParamsLocalStorageParsed = [queryParamsCurrentFiltered];
                }
            }
        } else {
            // First time seeing parameters
            queryParamsLocalStorageParsed = [queryParamsCurrentFiltered];
            newVal = true;
            log.info('[saveVisitQueryParams] First time saving query parameters');
        }

        // CRITICAL FIX: Save to sessionStorage (not localStorage!)
        // TTL: 7776000000ms = 90 days
        // Only save non-null parameters
        setWithExpiry("latestQueryStringParameters", queryParamsCurrentFiltered, 7776000000, 'session');
        log.info('[saveVisitQueryParams] Saved to sessionStorage (non-null only):', queryParamsCurrentFiltered);

        // Keep historical array in localStorage
        localStorage.setItem("queryParamsArrayLocalStorage", JSON.stringify(queryParamsLocalStorageParsed));
        log.debug('[saveVisitQueryParams] Saved to localStorage array, count:', queryParamsLocalStorageParsed?.length);

        // Verify it was saved
        const verified = getWithExpiry("latestQueryStringParameters");
        if (verified) {
            log.log('[saveVisitQueryParams] ✓ Successfully verified storage:', verified);
        } else {
            log.error('[saveVisitQueryParams] ✗ Failed to verify storage!');
        }
    } else {
        log.debug('[saveVisitQueryParams] No valid query parameters found, skipping save');
    }

    log.groupEnd();
}

/**
 * Parse query string with default key structure
 */
function parseQueryStringToObjectWithDefaultKeys(queryString, defaultKeys = []) {
  const start = performance.now();
  const log = createLogger('ParseQuery');
  log.groupCollapsed(
    `[ParseQuery] Parsing query string → "${queryString?.substring(0, 80) || 'empty'}"`
  );

  try {
    // Validate input - must be string, null, or undefined
    if (queryString !== null && queryString !== undefined && typeof queryString !== 'string') {
      log.error('[ParseQuery] Invalid input type:', typeof queryString, queryString);
      throw new TypeError(`Expected string, null, or undefined, but got ${typeof queryString}`);
    }

    // URLSearchParams handles null/undefined gracefully, but log for debugging
    if (!queryString) {
      log.debug('[ParseQuery] Empty or null query string provided');
    }

    // Extract query string from full URL if needed
    let queryPortion = queryString;
    if (queryString && queryString.includes('?')) {
      queryPortion = queryString.split('?')[1];
      log.debug('[ParseQuery] Extracted query portion from URL:', queryPortion);
    }

    const params = new URLSearchParams(queryPortion);
    const result = Object.fromEntries(defaultKeys.map(k => [k, params.get(k) || null]));

    log.debug('[ParseQuery] Parsed result:', result);

    const time = (performance.now() - start).toFixed(2);
    log.log(`[ParseQuery] Completed in ${time}ms`);
    return result;
  } catch (err) {
    log.error('[ParseQuery] Error parsing query string:', err, { queryString, type: typeof queryString });
    return Object.fromEntries(defaultKeys.map(k => [k, null]));
  } finally {
    log.groupEnd();
  }
}
async function getIPAddress() {
  const CACHE_KEY = 'userIP';
  const ONE_HOUR = 3600000; // 60 * 60 * 1000; // 1 hour in milliseconds
  const FIFTEEN_MINUTES = 900000; // 15 * 60 * 1000; // 15 minutes in milliseconds
  const log = createLogger('GetIPAddress');

  try {
    // Check if we have cached data
    const cached = sessionStorage.getItem(CACHE_KEY);

    if (cached) {
      const data = JSON.parse(cached);
      const age = Date.now() - data.timestamp;

      // If less than 1 hour old, return cached IP
      if (age < FIFTEEN_MINUTES) {
        log.log('Using cached IP:', data.ip);
        return data.ip;
      }

      log.log('Cached IP expired, fetching new...');
    }

    // Fetch new IP (either no cache or expired)
    const response = await fetch('https://api.ipify.org?format=json');
    const result = await response.json();

    // Store in session storage with timestamp
    const cacheData = {
      ip: result.ip,
      timestamp: Date.now()
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

    log.log('Fetched and cached new IP:', result.ip);
    return result.ip;

  } catch (error) {
    log.error('Failed to get IP:', error);

    // If fetch fails but we have cached data (even if expired), use it
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      log.log('Using stale cached IP due to error:', data.ip);
      return data.ip;
    }

    return null;
  }
}



// Export new utility functions
export { getExpectedTrackingKeys, getCurrentTrackingParams };
